import { ReadinessBanner } from './ReadinessBanner.js';
import { useEffect, useState } from 'react';
import { friendlyError } from '../../shared/api/errors.js';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  COUNTRY_DEFAULTS,
  DEFAULT_AVAILABILITY,
  SLOT_GRANULARITY_OPTIONS,
  defaultGreeting,
  effectiveGreeting,
  usesDefaultGreeting,
  type AssistantVoice,
  type Availability,
  type Country,
  type GreetingMode,
  type Language,
} from '@acs/shared';
import {
  Banner,
  Button,
  Card,
  ConfirmDialog,
  Field,
  Icon,
  Input,
  PageHeader,
  SaveBar,
  Select,
  SideNav,
  Skeleton,
  useToast,
} from '@acs/ui';
import type { IconName } from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { useArchiveBusiness, useSetBusinessRegion } from '../businesses/index.js';
import { BillingSection } from '../billing/index.js';
import { BusinessTypeField, CountryField, NumberSettings } from '../onboarding/index.js';
import { ClosedDatesCard } from '../staff/index.js';
import { BillingNotificationsCard } from './BillingNotificationsCard.js';
import { NotificationsCard } from './NotificationsCard.js';
import { OwnerEmailCard } from './OwnerEmailCard.js';
import { ServicesEditor } from './ServicesEditor.js';
import { WeeklyScheduleEditor } from '../schedule-grid/index.js';
import { useAvailability, useSaveAvailability, useSaveSettings } from './hooks.js';
import { AssistantFields, FactsFields } from './AssistantFields.js';
import { NOTICE_CHOICES, noticeKey, noticeSetting, type NoticeSetting } from './bookingNotice.js';
import { businessRenamePatch } from './rename.js';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';

/** Canonical timezone per business region. Single source of truth in
 * `@acs/shared` — this used to be a third hand-maintained copy of the same map
 * (onboarding, business creation, here), which is exactly how a business ends up
 * scheduling Warsaw appointments on Paris time. */
const REGION_TZ = (region: Country): string => COUNTRY_DEFAULTS[region].timezone;

export type Section = 'business' | 'number' | 'assistant' | 'services' | 'hours' | 'messages' | 'billing';

/** Rail order = the order this array is written in; the first entry is also the
 *  default panel. It mirrors the setup wizard: the business, what it sells and
 *  when it's open, then its number, then how the AI should sound. Number sits
 *  directly above Assistant because they are the two halves of "can it take a
 *  call" — the line, then the voice on it. */
const SECTIONS: { value: Section; labelKey: TranslationKey; icon: IconName }[] = [
  { value: 'business', labelKey: 'settings.section.business', icon: 'building' },
  { value: 'services', labelKey: 'settings.section.services', icon: 'star' },
  { value: 'hours', labelKey: 'settings.section.hours', icon: 'clock' },
  { value: 'number', labelKey: 'settings.section.number', icon: 'hash' },
  { value: 'assistant', labelKey: 'settings.section.assistant', icon: 'mic' },
  // Last, and after Assistant on purpose: SMS is what the business says to its
  // clients when nobody is on the phone, so it reads as the continuation of the
  // assistant rather than a separate product.
  { value: 'messages', labelKey: 'settings.section.messages', icon: 'bell' },
  // Last of all. The only panel that is about the account rather than about
  // answering the phone, and the one an owner visits least.
  { value: 'billing', labelKey: 'settings.section.billing', icon: 'file-text' },
];

/**
 * Sections that used to have their own rail entry and now live at the bottom of
 * a related one. Kept as redirects so a bookmarked `?section=closed` lands on
 * the panel that actually contains closed dates, instead of silently falling
 * back to Business and looking like the feature was removed.
 */
const MERGED_SECTIONS: Record<string, Section> = {
  closed: 'hours', // closed dates are an exception to opening hours
  danger: 'business', // archiving is the end of a business's life
  facts: 'assistant', // what the assistant knows sits with how it sounds
  // Subscription and minutes are one place since doc 42: `billing` is the
  // canonical section. Old bookmarks, SMS links and Stripe portal sessions
  // created before the change still carry `?section=subscription` and must
  // land there. (The reverse alias `billing → subscription` is gone, so the
  // two can never redirect into each other.)
  subscription: 'billing',
};

const isSection = (v: string | null): v is Section => SECTIONS.some((s) => s.value === v);

/** The panel to show for a `?section=` value, following merge redirects. */
export function resolveSection(requested: string | null): Section {
  if (isSection(requested)) return requested;
  return (requested && MERGED_SECTIONS[requested]) || 'business';
}

/**
 * Settings (design template — Portal · Settings): a section rail on the left,
 * one focused panel at a time on the right, so the page never becomes the wall
 * of stacked cards it used to be.
 */
export function SettingsPage() {
  const { t } = useI18n();
  const business = useActiveBusiness();
  // The section lives in the URL so it can be linked to — the shell's setup
  // banner and the dashboard's empty state both deep-link to Business.
  const [params, setParams] = useSearchParams();
  const section = resolveSection(params.get('section'));

  return (
    <>
      {/* The business is named in the TITLE, not just the subtitle: every panel
          here edits one business, and an owner with several needs to see which
          one before they change anything. */}
      <PageHeader
        title={t('settings.title', { business: business.name })}
        subtitle={t('settings.subtitle')}
      />
      <ReadinessBanner businessId={business.id} />

      <div className="settings-layout">
        <SideNav
          aria-label={t('settings.sections')}
          value={section}
          onChange={(v) => setParams({ section: v }, { replace: true })}
          items={SECTIONS.map((s) => ({
            value: s.value,
            label: t(s.labelKey),
            icon: <Icon name={s.icon} size={18} />,
          }))}
        />

        {/* Two panels stack more than one card. Both pairs are the same subject
            read to its end — hours then the dates that override them, the
            business then the button that retires it — rather than separate rail
            entries the owner has to know exist. Destructive actions stay LAST on
            their panel, below everything routine. */}
        <div className="grid" style={{ minWidth: 0 }}>
          {section === 'business' && (
            <>
              <BusinessCard business={business} />
              <DangerZone business={business} />
            </>
          )}
          {section === 'number' && <NumberSettings />}
          {section === 'services' && <ServicesEditor businessId={business.id} />}
          {section === 'hours' && (
            <>
              <HoursCard businessId={business.id} />
              <BookingGridCard business={business} />
              <ClosedDatesCard businessId={business.id} />
            </>
          )}
          {section === 'assistant' && (
            <>
              <AssistantCard business={business} />
              <BookingNoticeCard businessId={business.id} />
              <FactsCard business={business} />
            </>
          )}
          {/* Client messages first, then the owner's own. Two cards rather than
              one: what a business's clients receive is priced per message, what
              the owner receives is not, and one card invites the reading that
              switching on the second one turns on the first one's costs. */}
          {section === 'messages' && (
            <>
              <NotificationsCard />
              <OwnerEmailCard />
              <BillingNotificationsCard />
            </>
          )}
          {section === 'billing' && <BillingSection business={business} />}
        </div>
      </div>
    </>
  );
}

/**
 * Who the business is: the name you know it by, and the country that decides
 * what number it holds and which timezone it schedules in.
 *
 * The country is applied through its own callable rather than the shared
 * settings write, because the server owns the country→timezone pairing and
 * refuses the change once a number exists. It is committed on its own — a
 * rename and a region change are different-sized decisions and shouldn't share
 * one Save.
 */
export function BusinessCard({ business }: { business: BusinessDoc }) {
  const { t } = useI18n();
  const save = useSaveSettings(business.id);
  const setRegionMutation = useSetBusinessRegion(business.id);
  const { toast } = useToast();
  const [name, setName] = useState(business.name);
  const [region, setRegion] = useState<Country>(business.country);
  // A blank or whitespace-only edit is not a rename; trailing spaces are not
  // part of the name callers hear.
  const renamed = name.trim().length > 0 && name.trim() !== business.name;
  const rename = businessRenamePatch(business, name);

  // A forward number is always national (doc 13a), so its country IS this
  // business's country — changing one without the other would mean diverting a
  // line abroad on every call.
  const numberHeld = business.forwardNumber?.phoneNumber ?? null;
  const regionLocked = numberHeld !== null;
  const regionChanged = region !== business.country;

  useEffect(() => setRegion(business.country), [business.country]);

  return (
    <Card title={t('settings.section.business')}>
      <p className="field-hint" style={{ marginBottom: 16 }}>{t('settings.business.hint')}</p>

      <Field label={t('clients.name')} required>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      {/* What the rename does to the greeting, before Save (doc 68): ours is
          re-rendered with the new name in the same write; the owner's own words
          are left alone, but if they still say the old name we say so. */}
      {renamed && rename.greeting.kind === 'regenerated' && (
        <p className="field-hint" style={{ marginBottom: 16 }}>
          {t('settings.business.greetingFollows', { greeting: rename.greeting.greeting })}
        </p>
      )}
      {renamed && rename.greeting.kind === 'custom' && (rename.greeting.mentionsOldName || rename.greeting.staleName) && (
        <div style={{ marginBottom: 16 }}>
          <Banner tone="warning">
            {rename.greeting.mentionsOldName
              ? t('settings.business.greetingMentionsOldName', { name: business.name })
              : t('settings.business.greetingStaleName', { name: rename.greeting.staleName ?? '' })}
          </Banner>
        </div>
      )}

      {name !== business.name && (
        <SaveBar
          message={t('settings.business.nameUnsaved')}
          saveLabel={t('common.saveChanges')}
          discardLabel={t('common.discard')}
          regionLabel={t('ui.unsavedChanges')}
          saving={save.isPending}
          saveDisabled={!renamed}
          onDiscard={() => setName(business.name)}
          onSave={() =>
            save.mutate(rename.patch, {
              onSuccess: () => toast({ title: t('settings.business.saved'), tone: 'success' }),
            })
          }
        />
      )}
      {save.isError && <p className="field-error" style={{ marginTop: 10 }}>{t('settings.saveFailed')}</p>}

      {/* Always read-only here (doc 23). The one way to change it is to archive
          this business and create a new one — said in the field's own hint
          rather than left as a dead control the owner pokes at. */}
      <BusinessTypeField value={business.businessType} onChange={() => {}} locked />

      <CountryField
        country={region}
        onCountry={setRegion}
        savedCountry={business.country}
        locked={regionLocked}
      />

      {regionChanged && !regionLocked && (
        <>
          <div style={{ marginBottom: 16 }}>
            <Banner tone="warning">
              {t('settings.business.regionWarning', { region, timezone: REGION_TZ(region) })}
            </Banner>
          </div>
          <SaveBar
            message={t('settings.business.regionUnsaved', { region })}
            saveLabel={t('common.saveChanges')}
            discardLabel={t('common.discard')}
            regionLabel={t('ui.unsavedChanges')}
            saving={setRegionMutation.isPending}
            onDiscard={() => setRegion(business.country)}
            onSave={() =>
              setRegionMutation.mutate(region, {
                onSuccess: () => toast({ title: t('settings.business.moved', { region }), tone: 'success' }),
              })
            }
          />
        </>
      )}
      {setRegionMutation.isError && (
        <div style={{ marginTop: 10 }}>
          <Banner tone="danger">
            {friendlyError(t, setRegionMutation.error, t('settings.countryFailed'))}
          </Banner>
        </div>
      )}
    </Card>
  );
}

/**
 * What the assistant knows about the business beyond its services and hours
 * (doc 23 P1) — the address, then everything else. The fields themselves are
 * {@link FactsFields}, shared with the setup wizard.
 *
 * Sits under Assistant rather than in a rail entry of its own: how the
 * assistant sounds and what it is allowed to say are one subject read in two
 * steps, and a separate entry made an owner hunt for the answer to "why did it
 * tell my customer it didn't know where we are".
 */
function FactsCard({ business }: { business: BusinessDoc }) {
  const { t } = useI18n();
  const save = useSaveSettings(business.id);
  const { toast } = useToast();
  const [address, setAddress] = useState(business.facts.address);
  const [text, setText] = useState(business.facts.text);

  const dirty = address !== business.facts.address || text !== business.facts.text;

  const reset = () => {
    setAddress(business.facts.address);
    setText(business.facts.text);
  };

  return (
    <Card title={t('settings.facts.title')}>
      <p className="field-hint" style={{ marginBottom: 16 }}>{t('settings.facts.hint')}</p>

      <FactsFields address={address} onAddress={setAddress} text={text} onText={setText} />

      {dirty && (
        <SaveBar
          message={t('common.unsavedChanges')}
          saveLabel={t('common.saveChanges')}
          discardLabel={t('common.discard')}
          regionLabel={t('ui.unsavedChanges')}
          saving={save.isPending}
          onDiscard={reset}
          onSave={() =>
            save.mutate(
              { facts: { address: address.trim(), text: text.trim() } },
              { onSuccess: () => toast({ title: t('settings.assistant.saved'), tone: 'success' }) },
            )
          }
        />
      )}
    </Card>
  );
}

/** How the receptionist behaves on a call — see {@link AssistantFields}, which
 *  the setup wizard's assistant step renders too. */
export function AssistantCard({ business }: { business: BusinessDoc }) {
  const { t } = useI18n();
  const save = useSaveSettings(business.id);
  const { toast } = useToast();
  // Doc 71: a business stored before modes existed reads as standard only
  // when its text is our sentence for its CURRENT name; anything else is its own.
  const savedMode: GreetingMode = usesDefaultGreeting(business) ? 'default' : 'custom';
  const [language, setLanguage] = useState<Language>(business.language);
  const [greetingMode, setGreetingMode] = useState<GreetingMode>(savedMode);
  // Switching to own words starts from what callers hear now.
  const [greeting, setGreeting] = useState(savedMode === 'custom' ? business.greeting : effectiveGreeting(business));
  const [voice, setVoice] = useState<AssistantVoice>(business.voice);
  const [maxCallSeconds, setMaxCallSeconds] = useState(business.maxCallSeconds);

  const dirty =
    language !== business.language ||
    greetingMode !== savedMode ||
    (greetingMode === 'custom' && greeting !== business.greeting) ||
    voice !== business.voice ||
    maxCallSeconds !== business.maxCallSeconds;

  const reset = () => {
    setLanguage(business.language);
    setGreetingMode(savedMode);
    setGreeting(savedMode === 'custom' ? business.greeting : effectiveGreeting(business));
    setVoice(business.voice);
    setMaxCallSeconds(business.maxCallSeconds);
  };

  return (
    <Card title={t('settings.assistant.title')}>
      <p className="field-hint" style={{ marginBottom: 16 }}>{t('settings.assistant.hint')}</p>

      <AssistantFields
        language={language}
        // The standard greeting follows the language by itself (its preview
        // re-renders); the owner's own words stay as written (doc 68, doc 71).
        onLanguage={setLanguage}
        voice={voice}
        onVoice={setVoice}
        greeting={greeting}
        onGreeting={setGreeting}
        greetingMode={greetingMode}
        onGreetingMode={setGreetingMode}
        businessName={business.name}
        maxCallSeconds={maxCallSeconds}
        onMaxCallSeconds={setMaxCallSeconds}
      />

      {dirty && (
        <SaveBar
          message={t('common.unsavedChanges')}
          saveLabel={t('common.saveChanges')}
          discardLabel={t('common.discard')}
          regionLabel={t('ui.unsavedChanges')}
          saving={save.isPending}
          onDiscard={reset}
          onSave={() =>
            save.mutate(
              {
                language,
                greetingMode,
                // The standard greeting is stored rendered too — for older
                // readers; publication and calls render it from the name.
                greeting: greetingMode === 'default' ? defaultGreeting(business.name.trim(), language) : greeting,
                voice,
                maxCallSeconds,
              },
              { onSuccess: () => toast({ title: t('settings.assistant.saved'), tone: 'success' }) },
            )
          }
        />
      )}
      {save.isError && <p className="field-error" style={{ marginTop: 10 }}>{t('settings.saveFailed')}</p>}
    </Card>
  );
}

/**
 * How soon before an appointment a caller can still book it (doc 84).
 *
 * Stored with the hours (`availability/weekly`) because the booking check reads
 * it from there, but shown under Assistant: it is a rule the assistant applies
 * and explains on the phone — it never offers a sooner time and tells the
 * caller why — not a property of the opening hours. Owner bookings from the
 * calendar are not limited by it.
 *
 * A business on a value no choice matches (set before this control existed)
 * keeps its own option, so opening the page cannot silently change it.
 *
 * The save writes the whole availability document, so it waits for that
 * document: `null` (none yet) means the implicit default hours, which are
 * written as they are; `undefined` (still loading, or the read failed) offers
 * nothing to save — a notice change must never put default hours over real ones.
 */
export function BookingNoticeCard({ businessId }: { businessId: string }) {
  const { t } = useI18n();
  const { data, isLoading } = useAvailability(businessId);
  const save = useSaveAvailability(businessId);
  const { toast } = useToast();
  const current = data ?? DEFAULT_AVAILABILITY;
  const stored: NoticeSetting = { minNoticeMin: current.minNoticeMin, minNoticeDays: current.minNoticeDays };
  const storedKey = noticeKey(stored);
  const [choice, setChoice] = useState(storedKey);
  // An unsaved pick survives a snapshot from elsewhere; only an untouched
  // control follows the stored value.
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!touched) setChoice(storedKey);
  }, [storedKey, touched]);
  const dirty = choice !== storedKey;

  if (isLoading || data === undefined) {
    return (
      <Card title={t('settings.notice.title')}>
        <Skeleton height={64} radius="var(--tc-radius-sm)" />
      </Card>
    );
  }

  return (
    <Card title={t('settings.notice.title')}>
      <p className="field-hint" style={{ marginBottom: 16 }}>{t('settings.notice.hint')}</p>

      <Field label={t('settings.notice.label')} hint={t('settings.notice.fieldHint')}>
        <Select
          value={choice}
          onChange={(e) => {
            setChoice(e.target.value);
            setTouched(true);
          }}
        >
          {NOTICE_CHOICES.map((c) => (
            <option key={c.key} value={c.key}>
              {t(c.labelKey)}
            </option>
          ))}
          {storedKey === 'custom' && (
            <option value="custom">
              {stored.minNoticeDays > 0 && stored.minNoticeMin > 0
                ? t('settings.notice.customBoth', { days: stored.minNoticeDays, minutes: stored.minNoticeMin })
                : stored.minNoticeDays > 0
                  ? t('settings.notice.customDays', { days: stored.minNoticeDays })
                  : t('settings.notice.customMinutes', { minutes: stored.minNoticeMin })}
            </option>
          )}
        </Select>
      </Field>

      {dirty && (
        <SaveBar
          message={t('common.unsavedChanges')}
          saveLabel={t('common.saveChanges')}
          discardLabel={t('common.discard')}
          regionLabel={t('ui.unsavedChanges')}
          saving={save.isPending}
          onDiscard={() => {
            setChoice(storedKey);
            setTouched(false);
          }}
          onSave={() =>
            save.mutate(
              { ...current, ...noticeSetting(choice, stored) },
              {
                onSuccess: () => {
                  setTouched(false);
                  toast({ title: t('settings.notice.saved'), tone: 'success' });
                },
              },
            )
          }
        />
      )}
      {save.isError && <p className="field-error" style={{ marginTop: 10 }}>{t('settings.saveFailed')}</p>}
    </Card>
  );
}

/**
 * How finely a caller may pick a time (doc 29).
 *
 * `slotGranularityMin` has always driven the booking grid and has never had a
 * control — the only way to change it was editing Firestore by hand, so every
 * business ran on the 30-minute default whether or not it suited them. A
 * ten-minute grid lets someone take 10:10 for a quick service; an hour-and-a-half
 * one keeps a long treatment from being offered at times it cannot finish.
 *
 * Lives beside the hours because it is the same subject read to its end: when
 * you are open, then how the open time is cut up. Changing it never moves an
 * existing appointment — it only changes which starts are offered next.
 */
function BookingGridCard({ business }: { business: BusinessDoc }) {
  const { t } = useI18n();
  const save = useSaveSettings(business.id);
  const { toast } = useToast();
  const [granularity, setGranularity] = useState(business.slotGranularityMin);

  useEffect(() => setGranularity(business.slotGranularityMin), [business.slotGranularityMin]);
  const dirty = granularity !== business.slotGranularityMin;

  // 90 and 60 get words rather than "90 min": an owner thinks in hours once the
  // step passes one, and "1 h 30 min" is what they would say out loud.
  const label = (min: number) =>
    min === 90 ? t('settings.booking.hourAndHalf')
    : min === 60 ? t('settings.booking.hour')
    : t('settings.booking.minutes', { count: min });

  return (
    <Card title={t('settings.booking.title')}>
      <p className="field-hint" style={{ marginBottom: 16 }}>{t('settings.booking.hint')}</p>

      <Field
        label={t('settings.booking.granularity')}
        hint={t('settings.booking.granularityHint')}
      >
        <Select value={String(granularity)} onChange={(e) => setGranularity(Number(e.target.value))}>
          {SLOT_GRANULARITY_OPTIONS.map((min) => (
            <option key={min} value={min}>
              {label(min)}
            </option>
          ))}
          {/* A business already on a step we no longer offer keeps its own
              option, so opening this page cannot silently re-cut its day. */}
          {!SLOT_GRANULARITY_OPTIONS.includes(
            business.slotGranularityMin as (typeof SLOT_GRANULARITY_OPTIONS)[number],
          ) && (
            <option value={business.slotGranularityMin}>{label(business.slotGranularityMin)}</option>
          )}
        </Select>
      </Field>

      {dirty && (
        <SaveBar
          message={t('common.unsavedChanges')}
          saveLabel={t('common.saveChanges')}
          discardLabel={t('common.discard')}
          regionLabel={t('ui.unsavedChanges')}
          saving={save.isPending}
          onDiscard={() => setGranularity(business.slotGranularityMin)}
          onSave={() =>
            save.mutate(
              { slotGranularityMin: granularity },
              { onSuccess: () => toast({ title: t('settings.booking.saved'), tone: 'success' }) },
            )
          }
        />
      )}
      {save.isError && <p className="field-error" style={{ marginTop: 10 }}>{t('settings.saveFailed')}</p>}
    </Card>
  );
}

/** Business hours are the FALLBACK (doc 09/10): used for staff without a
 * personal schedule, and for all bookings when there are no staff. Edited with
 * the shared multi-interval grid, which supports split shifts. */
function HoursCard({ businessId }: { businessId: string }) {
  const { t } = useI18n();
  const { data, isLoading } = useAvailability(businessId);
  const save = useSaveAvailability(businessId);
  const { toast } = useToast();
  const [hours, setHours] = useState<Availability['hours']>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data && !dirty) setHours(data.hours);
    else if (!data && !isLoading && !dirty) setHours(DEFAULT_AVAILABILITY.hours);
  }, [data, isLoading, dirty]);

  if (isLoading) {
    return (
      <Card title={t('settings.hours.title')}>
        <Skeleton height={280} radius="var(--tc-radius-sm)" />
      </Card>
    );
  }

  return (
    <Card title={t('settings.hours.title')}>
      <p className="field-hint" style={{ marginBottom: 12 }}>{t('settings.hours.hint')}</p>
      <WeeklyScheduleEditor
        value={hours}
        onChange={(h) => {
          setHours(h);
          setDirty(true);
        }}
      />
      {dirty && (
        <SaveBar
          message={t('common.unsavedChanges')}
          saveLabel={t('common.saveChanges')}
          discardLabel={t('common.discard')}
          regionLabel={t('ui.unsavedChanges')}
          saving={save.isPending}
          onDiscard={() => {
            setHours(data?.hours ?? DEFAULT_AVAILABILITY.hours);
            setDirty(false);
          }}
          onSave={() =>
            save.mutate(
              { ...(data ?? DEFAULT_AVAILABILITY), hours },
              {
                onSuccess: () => {
                  setDirty(false);
                  toast({ title: t('settings.hours.saved'), tone: 'success' });
                },
              },
            )
          }
        />
      )}
    </Card>
  );
}

/** Archiving is off for now — see {@link DangerZone}. */
const ARCHIVE_ENABLED = false;

/**
 * Archive this business (doc 07 §4.3). `archiveBusiness` releases the forward
 * number itself, so holding one is no longer a reason to block the button — it
 * used to be, which left every active business permanently un-archivable behind
 * a banner pointing at a release control that no longer exists. The server still
 * refuses if regulated, bundle-backed numbers are held; that comes back as an
 * error below rather than as a pre-emptive guess here.
 */
function DangerZone({ business }: { business: BusinessDoc }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const archive = useArchiveBusiness();
  const [confirm, setConfirm] = useState(false);

  // Archiving is HIDDEN for now: the callable, the copy and the confirmation
  // all still work, but the only way in is off screen until we are ready to
  // support what it does to a live line. Kept whole rather than deleted so
  // turning it back on is one line, not a re-implementation.
  if (!ARCHIVE_ENABLED) return null;

  if (business.status === 'archived') return null;

  return (
    <Card title={t('settings.danger.title')}>
      <div className="danger-row">
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontWeight: 600 }}>{t('settings.danger.archive')}</div>
          <p className="muted" style={{ fontSize: 13, margin: '4px 0 0' }}>{t('settings.danger.archiveHint')}</p>
        </div>
        <Button variant="danger" onClick={() => setConfirm(true)}>
          {t('settings.danger.confirmLabel')}
        </Button>
      </div>

      {archive.isError && (
        <div style={{ marginTop: 14 }}>
          <Banner tone="danger">{friendlyError(t, archive.error, t('settings.danger.archiveFailed'))}</Banner>
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          title={t('settings.danger.confirmTitle', { business: business.name })}
          description={t('settings.danger.confirmDescription')}
          icon={<Icon name="alert-triangle" size={24} />}
          confirmLabel={t('settings.danger.confirmLabel')}
          cancelLabel={t('common.cancel')}
          loading={archive.isPending}
          onCancel={() => setConfirm(false)}
          onConfirm={() => archive.mutate(business.id, { onSuccess: () => navigate('/businesses') })}
        />
      )}
    </Card>
  );
}
